// Fetches a BMD session's runtime state: which topics are queued, which is
// current, which question the student should answer next, and any unresolved
// intervention flags. It is the source of truth for BmdTestPlayer when
// deciding what to render.
//
// The caller gets a `refetch()` it must call after any state-mutating action
// (submitAnswer, skipQuestion, completeTopic) so the UI re-syncs with the
// server's view of the session.

import { useEffect, useState } from "react"
import { getBmdSessionState } from "../../modules/diagnostics/services/bmd.service"
import type { BmdSessionState } from "../../modules/diagnostics/types/bmd.types"

export type UseBmdSessionState = {
  session: BmdSessionState | null
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useBmdSession(
  diagnosticsSessionKey: string | null | undefined,
): UseBmdSessionState {
  const [session, setSession] = useState<BmdSessionState | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [refetchCounter, setRefetchCounter] = useState<number>(0)

  useEffect(() => {
    if (!diagnosticsSessionKey) {
      setSession(null)
      setLoading(false)
      setError(null)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setError(null)

    getBmdSessionState(
      { diagnostics_session_key: diagnosticsSessionKey },
      controller.signal,
    )
      .then((data) => {
        setSession(data)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load BMD session")
        setSession(null)
        setLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [diagnosticsSessionKey, refetchCounter])

  const refetch = () => setRefetchCounter((prev) => prev + 1)

  return { session, loading, error, refetch }
}
